import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  useLocation,
  useNavigate,
} from 'react-router-dom';

import {
  MapContainer,
  Marker,
  Polyline,
  Popup,
  TileLayer,
  ZoomControl,
} from 'react-leaflet';

import L from 'leaflet';
import axios from 'axios';

import 'leaflet/dist/leaflet.css';

import styles from './style/Home.module.css';

delete (L.Icon.Default.prototype as any)
  ._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: require(
    'leaflet/dist/images/marker-icon-2x.png'
  ),

  iconUrl: require(
    'leaflet/dist/images/marker-icon.png'
  ),

  shadowUrl: require(
    'leaflet/dist/images/marker-shadow.png'
  ),
});

const redIcon = new L.Icon({
  iconUrl: require(
    './style/marker-icon-red.png'
  ),

  iconSize: [25, 41],

  iconAnchor: [12, 41],

  popupAnchor: [1, -34],

  shadowUrl: require(
    'leaflet/dist/images/marker-shadow.png'
  ),

  shadowSize: [41, 41],
});

const blueIcon = new L.Icon.Default();

const createEventIcon = (
  event: DeviceEvent
): L.DivIcon => {
  const isLoad =
    event === 'LOAD';

  const backgroundColor =
    isLoad
      ? '#0066ff'
      : '#00a651';

  const symbol =
    isLoad
      ? '↓'
      : '↑';

  const title =
    isLoad
      ? 'Load'
      : 'Unload';

  return L.divIcon({
    className: '',

    html: `
      <div
        title="${title}"
        style="
          width: 34px;
          height: 34px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          border: 3px solid #ffffff;
          background-color: ${backgroundColor};
          color: #ffffff;
          font-size: 24px;
          font-weight: bold;
          line-height: 1;
          box-shadow: 0 2px 7px rgba(0, 0, 0, 0.45);
        "
      >
        ${symbol}
      </div>
    `,

    iconSize: [34, 34],

    iconAnchor: [17, 17],

    popupAnchor: [0, -20],
  });
};

type Device = {
  id_device: string;
  time_last_update: string;
  battery_status: string;
  level: string;
  latitude: number;
  longitude: number;
  battery_limit: number;
  level_limit: number;
  affiliation: boolean;
};

type DeviceEvent =
  | 'MOVE'
  | 'LOAD'
  | 'UNLOAD';

type DevicePosition = {
  latitude: number;
  longitude: number;
  event: DeviceEvent;
  level: number;
  level_2: number;
  level_3: number;
  level_4: number;
  level_5: number;
  created_at: string;
};

type RouteSegment = {
  positions: [number, number][];
  color: string;
  activeEvent: DeviceEvent;
};

type User = {
  id: number;
  username: string;
  admin: boolean;
};

const formatDateTime = (
  value?: string
): string => {
  if (!value) {
    return 'Date not available';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString(
    'en-GB',
    {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }
  );
};

const normalizeDeviceEvent = (
  value?: string
): DeviceEvent => {
  const normalizedValue = String(
    value || 'MOVE'
  )
    .trim()
    .toUpperCase();

  switch (normalizedValue) {
    case 'LOAD':
      return 'LOAD';

    /*
     * Compatibility with existing database records.
     * Both old unload event types are now displayed
     * simply as UNLOAD.
     */
    case 'UNLOAD':
    case 'UNLOAD_OK':
    case 'UNLOAD_FRAUD':
      return 'UNLOAD';

    default:
      return 'MOVE';
  }
};

const getEventColor = (
  event: DeviceEvent
): string => {
  switch (event) {
    case 'LOAD':
      return '#0066ff';

    case 'UNLOAD':
      return '#00a651';

    default:
      return '#777777';
  }
};

const getEventDescription = (
  event: DeviceEvent
): string => {
  switch (event) {
    case 'LOAD':
      return 'Load';

    case 'UNLOAD':
      return 'Unload';

    default:
      return 'Movement';
  }
};

const Home: React.FC = () => {
  const navigate = useNavigate();

  const location = useLocation();

  const tenant = useMemo(() => {
    const params =
      new URLSearchParams(
        location.search
      );

    return (
      params.get('tenant')?.trim() ||
      'tenant_a'
    );
  }, [location.search]);

  const [
    devices,
    setDevices,
  ] = useState<Device[]>([]);

  const [
    warningDevices,
    setWarningDevices,
  ] = useState<Device[]>([]);

  const [
    user,
    setUser,
  ] = useState<User | null>(null);

  const [
    deviceRoutes,
    setDeviceRoutes,
  ] = useState<
    Record<string, DevicePosition[]>
  >({});

  const [
    selectedDeviceId,
    setSelectedDeviceId,
  ] = useState<string>('');

  const [
    showRoute,
    setShowRoute,
  ] = useState(true);

  const [
    showEvents,
    setShowEvents,
  ] = useState(true);

  const [
    showHistory,
    setShowHistory,
  ] = useState(false);

  const [
    dateFrom,
    setDateFrom,
  ] = useState('');

  const [
    dateTo,
    setDateTo,
  ] = useState('');

  useEffect(() => {
    const storedUser =
      localStorage.getItem('user');

    if (!storedUser) {
      return;
    }

    try {
      setUser(
        JSON.parse(storedUser)
      );
    } catch (error) {
      console.error(
        'Error reading user from localStorage:',
        error
      );

      localStorage.removeItem(
        'user'
      );
    }
  }, []);

  useEffect(() => {
    if (!showHistory) {
      return;
    }

    const handleKeyDown = (
      event: KeyboardEvent
    ) => {
      if (event.key === 'Escape') {
        setShowHistory(false);
      }
    };

    window.addEventListener(
      'keydown',
      handleKeyDown
    );

    return () => {
      window.removeEventListener(
        'keydown',
        handleKeyDown
      );
    };
  }, [showHistory]);

  const fetchDevicePositions = async (
    deviceIds: string[]
  ) => {
    try {
      const routes: Record<
        string,
        DevicePosition[]
      > = {};

      await Promise.all(
        deviceIds.map(
          async (deviceId) => {
            try {
              const response =
                await axios.get(
                  `/api/devices/${encodeURIComponent(
                    deviceId
                  )}/positions`,
                  {
                    headers: {
                      'X-Tenant-Key':
                        tenant,
                    },

                    withCredentials:
                      true,
                  }
                );

              const responseData =
                response.data;

              const rawPositions =
                Array.isArray(
                  responseData
                )
                  ? responseData
                  : Array.isArray(
                        responseData
                          ?.positions
                      )
                    ? responseData
                        .positions
                    : [];

              const positions =
                rawPositions
                  .map(
                    (
                      position: any
                    ): DevicePosition => ({
                      latitude: Number(
                        position.latitude
                      ),

                      longitude: Number(
                        position.longitude
                      ),

                      event:
                        normalizeDeviceEvent(
                          position.event
                        ),

                      level: Number(
                        position.level
                      ),

                      level_2: Number(
                        position.level_2
                      ),

                      level_3: Number(
                        position.level_3
                      ),

                      level_4: Number(
                        position.level_4
                      ),

                      level_5: Number(
                        position.level_5
                      ),

                      /*
                       * Backend compatibility:
                       *
                       * - created_at
                       * - ts
                       * - timestamp
                       * - time
                       */
                      created_at: String(
                        position.created_at ||
                          position.ts ||
                          position.timestamp ||
                          position.time ||
                          ''
                      ),
                    })
                  )
                  .filter(
                    (
                      position:
                        DevicePosition
                    ) =>
                      Number.isFinite(
                        position.latitude
                      ) &&
                      Number.isFinite(
                        position.longitude
                      ) &&
                      position.latitude >=
                        -90 &&
                      position.latitude <=
                        90 &&
                      position.longitude >=
                        -180 &&
                      position.longitude <=
                        180
                  )
                  .sort(
                    (
                      first,
                      second
                    ) => {
                      const firstTime =
                        new Date(
                          first.created_at
                        ).getTime();

                      const secondTime =
                        new Date(
                          second.created_at
                        ).getTime();

                      if (
                        Number.isNaN(
                          firstTime
                        ) ||
                        Number.isNaN(
                          secondTime
                        )
                      ) {
                        return 0;
                      }

                      return (
                        firstTime -
                        secondTime
                      );
                    }
                  );

              routes[
                String(deviceId)
              ] = positions;
            } catch (error) {
              console.error(
                `Error loading positions for plate ${deviceId}:`,
                error
              );

              routes[
                String(deviceId)
              ] = [];
            }
          }
        )
      );

      setDeviceRoutes(routes);
    } catch (error) {
      console.error(
        'Error loading position history:',
        error
      );

      setDeviceRoutes({});
    }
  };

  const fetchDevices = async () => {
    try {
      const response =
        await axios.get(
          '/api/devices',
          {
            headers: {
              'X-Tenant-Key':
                tenant,
            },

            withCredentials: true,
          }
        );

      const rawDevices:
        Device[] =
        Array.isArray(
          response.data?.devices
        )
          ? response.data.devices
          : Array.isArray(
                response.data
              )
            ? response.data
            : [];

      const data: Device[] =
        rawDevices.map(
          (device): Device => ({
            ...device,

            id_device: String(
              device.id_device
            ),

            latitude: Number(
              device.latitude
            ),

            longitude: Number(
              device.longitude
            ),

            battery_limit: Number(
              device.battery_limit
            ),

            level_limit: Number(
              device.level_limit
            ),

            affiliation:
              device.affiliation ===
                true ||
              String(
                device.affiliation
              ).toLowerCase() ===
                'true',
          })
        );

      setDevices(data);

      setSelectedDeviceId(
        (
          previousSelectedId
        ) => {
          const deviceStillExists =
            data.some(
              (device) =>
                device.id_device ===
                previousSelectedId
            );

          if (
            previousSelectedId &&
            deviceStillExists
          ) {
            return previousSelectedId;
          }

          return (
            data[0]?.id_device ||
            ''
          );
        }
      );

      await fetchDevicePositions(
        data.map(
          (device) =>
            device.id_device
        )
      );

      const now = new Date();

      const warnings =
        data.filter((device) => {
          if (
            device.affiliation !==
            true
          ) {
            return false;
          }

          const lastUpdate =
            new Date(
              device.time_last_update
            );

          const lastUpdateTimestamp =
            lastUpdate.getTime();

          const isValidDate =
            !Number.isNaN(
              lastUpdateTimestamp
            );

          const diffInMs =
            isValidDate
              ? now.getTime() -
                lastUpdateTimestamp
              : Number.POSITIVE_INFINITY;

          const diffInDays =
            diffInMs /
            (
              1000 *
              60 *
              60 *
              24
            );

          const isDisconnected =
            diffInDays >= 2;

          const batteryStatusNumber =
            Number(
              device.battery_status
            );

          const isLowBattery =
            Number.isFinite(
              batteryStatusNumber
            ) &&
            batteryStatusNumber <=
              Number(
                device.battery_limit
              );

          const levelNumber =
            Number(device.level);

          const isLowLevel =
            Number.isFinite(
              levelNumber
            ) &&
            levelNumber <=
              Number(
                device.level_limit
              );

          return (
            isDisconnected ||
            isLowBattery ||
            isLowLevel
          );
        });

      setWarningDevices(
        warnings
      );
    } catch (error) {
      console.error(
        'Error loading plates:',
        error
      );

      setDevices([]);

      setWarningDevices([]);

      setDeviceRoutes({});

      setSelectedDeviceId('');
    }
  };

  useEffect(() => {
    fetchDevices();

    const timer =
      window.setInterval(
        fetchDevices,
        5000
      );

    return () => {
      window.clearInterval(
        timer
      );
    };
  }, [tenant]);

  const selectedDevice =
    useMemo(() => {
      return devices.find(
        (device) =>
          device.id_device ===
          selectedDeviceId
      );
    }, [
      devices,
      selectedDeviceId,
    ]);

  const filteredPositions =
    useMemo(() => {
      const positions =
        deviceRoutes[
          selectedDeviceId
        ] || [];

      const fromTimestamp =
        dateFrom
          ? new Date(
              `${dateFrom}T00:00:00`
            ).getTime()
          : null;

      const toTimestamp =
        dateTo
          ? new Date(
              `${dateTo}T23:59:59.999`
            ).getTime()
          : null;

      return positions.filter(
        (position) => {
          if (
            !position.created_at
          ) {
            return (
              fromTimestamp ===
                null &&
              toTimestamp ===
                null
            );
          }

          const timestamp =
            new Date(
              position.created_at
            ).getTime();

          if (
            Number.isNaN(
              timestamp
            )
          ) {
            return (
              fromTimestamp ===
                null &&
              toTimestamp ===
                null
            );
          }

          if (
            fromTimestamp !==
              null &&
            timestamp <
              fromTimestamp
          ) {
            return false;
          }

          if (
            toTimestamp !==
              null &&
            timestamp >
              toTimestamp
          ) {
            return false;
          }

          return true;
        }
      );
    }, [
      deviceRoutes,
      selectedDeviceId,
      dateFrom,
      dateTo,
    ]);

  const visibleEvents =
    useMemo(() => {
      return filteredPositions.filter(
        (position) =>
          position.event ===
            'LOAD' ||
          position.event ===
            'UNLOAD'
      );
    }, [filteredPositions]);

  /*
   * The route is divided into multiple segments.
   *
   * After LOAD:
   * route is blue.
   *
   * After UNLOAD:
   * route is green.
   */
  const routeSegments =
    useMemo<RouteSegment[]>(() => {
      if (
        filteredPositions.length <
        2
      ) {
        return [];
      }

      const segments:
        RouteSegment[] = [];

      let activeEvent:
        DeviceEvent = 'MOVE';

      for (
        let index = 0;
        index <
        filteredPositions.length -
          1;
        index += 1
      ) {
        const currentPosition =
          filteredPositions[index];

        const nextPosition =
          filteredPositions[
            index + 1
          ];

        if (
          currentPosition.event !==
          'MOVE'
        ) {
          activeEvent =
            currentPosition.event;
        }

        const segmentColor =
          getEventColor(
            activeEvent
          );

        const currentCoordinate:
          [number, number] = [
          currentPosition.latitude,
          currentPosition.longitude,
        ];

        const nextCoordinate:
          [number, number] = [
          nextPosition.latitude,
          nextPosition.longitude,
        ];

        const lastSegment =
          segments[
            segments.length - 1
          ];

        if (
          lastSegment &&
          lastSegment.color ===
            segmentColor &&
          lastSegment.activeEvent ===
            activeEvent
        ) {
          lastSegment.positions.push(
            nextCoordinate
          );
        } else {
          segments.push({
            positions: [
              currentCoordinate,
              nextCoordinate,
            ],

            color:
              segmentColor,

            activeEvent,
          });
        }
      }

      return segments;
    }, [filteredPositions]);

  const isWarningDevice = (
    device: Device
  ): boolean => {
    return warningDevices.some(
      (warning) =>
        warning.id_device ===
        device.id_device
    );
  };

  const getBatteryStatusText = (
    batteryStatus: string,
    batteryLimit: number
  ): string => {
    const batteryStatusNumber =
      Number(batteryStatus);

    return Number.isFinite(
      batteryStatusNumber
    ) &&
      batteryStatusNumber <=
        Number(batteryLimit)
      ? 'Low'
      : 'OK';
  };

  const getLevelStatusText = (
    level: string,
    levelLimit: number
  ): string => {
    const levelNumber =
      Number(level);

    return Number.isFinite(
      levelNumber
    ) &&
      levelNumber <=
        Number(levelLimit)
      ? 'Low'
      : 'OK';
  };

  const resetHistoryFilters =
    () => {
      setDateFrom('');

      setDateTo('');

      setShowRoute(true);

      setShowEvents(true);
    };

  if (user && !user.admin) {
    return (
      <div
        style={{
          display: 'flex',
          height: '100vh',
          alignItems: 'center',
          justifyContent:
            'center',
          fontSize: '24px',
          fontWeight: 'bold',
          textAlign: 'center',
          padding: '2rem',
        }}
      >
        Sorry, your account has
        not yet been validated by
        an administrator.
      </div>
    );
  }

  return (
    <div
      className={
        styles.container
      }
    >
      <nav
        className={
          styles.topbar
        }
      >
        <button
          className={
            styles.navButton
          }
          onClick={() =>
            navigate(
              `/user?tenant=${encodeURIComponent(
                tenant
              )}`
            )
          }
        >
          Messages
        </button>

        <button
          className={
            styles.navButton
          }
          onClick={() =>
            navigate(
              `/devices?tenant=${encodeURIComponent(
                tenant
              )}`
            )
          }
        >
          Truck List
        </button>

        <button
          className={
            styles.navSettings
          }
          onClick={() =>
            navigate(
              `/settings?tenant=${encodeURIComponent(
                tenant
              )}`
            )
          }
        >
          New Track
        </button>

        <button
          className={
            styles.iconButton
          }
          onClick={() => {
            localStorage.removeItem(
              'user'
            );

            localStorage.removeItem(
              'fileUploaded'
            );

            navigate(
              `/login?tenant=${encodeURIComponent(
                tenant
              )}`
            );
          }}
          aria-label="Logout"
        >
          Logout
        </button>
      </nav>

      <main
        className={
          styles.mainContent
        }
      >
        <aside
          className={
            styles.warningPanel
          }
        >

          <div
            className={
              styles.deviceFilterBox
            }
            style={{
              display: 'grid',
              gap: '10px',
            }}
          >
            <h4>
              Route and event
              filters
            </h4>

            <label>
              Truck Plate

              <select
                value={
                  selectedDeviceId
                }
                onChange={(
                  event
                ) => {
                  setSelectedDeviceId(
                    event.target.value
                  );

                  setShowHistory(
                    false
                  );
                }}
                style={{
                  width: '100%',
                  marginTop: '4px',
                  padding: '7px',
                }}
              >
                <option value="">
                  Select a Truck Plate
                </option>

                {devices.map(
                  (device) => (
                    <option
                      key={
                        device.id_device
                      }
                      value={
                        device.id_device
                      }
                    >
                      Truck Plate{' '}
                      {
                        device.id_device
                      }
                    </option>
                  )
                )}
              </select>
            </label>

            <label>
              From date

              <input
                type="date"
                value={dateFrom}
                max={
                  dateTo ||
                  undefined
                }
                onChange={(
                  event
                ) =>
                  setDateFrom(
                    event.target.value
                  )
                }
                style={{
                  width: '100%',
                  marginTop: '4px',
                  padding: '7px',
                }}
              />
            </label>

            <label>
              To date

              <input
                type="date"
                value={dateTo}
                min={
                  dateFrom ||
                  undefined
                }
                onChange={(
                  event
                ) =>
                  setDateTo(
                    event.target.value
                  )
                }
                style={{
                  width: '100%',
                  marginTop: '4px',
                  padding: '7px',
                }}
              />
            </label>

            <label
              className={
                styles.deviceCheckbox
              }
            >
              <input
                type="checkbox"
                checked={showRoute}
                onChange={(
                  event
                ) =>
                  setShowRoute(
                    event.target.checked
                  )
                }
              />

              Show route
            </label>

            <label
              className={
                styles.deviceCheckbox
              }
            >
              <input
                type="checkbox"
                checked={showEvents}
                onChange={(
                  event
                ) =>
                  setShowEvents(
                    event.target.checked
                  )
                }
              />

              Show event markers
            </label>

            <button
              type="button"
              className={
                styles.filterButton
              }
              onClick={
                resetHistoryFilters
              }
            >
              Reset history
              filters
            </button>

            <button
              type="button"
              className={
                styles.filterButton
              }
              disabled={
                !selectedDeviceId
              }
              onClick={() =>
                setShowHistory(true)
              }
              style={{
                opacity:
                  selectedDeviceId
                    ? 1
                    : 0.6,

                cursor:
                  selectedDeviceId
                    ? 'pointer'
                    : 'not-allowed',
              }}
            >
              Open event history
            </button>

            <div>
              Route points:{' '}
              <strong>
                {
                  filteredPositions.length
                }
              </strong>
            </div>

            <div>
              Events found:{' '}
              <strong>
                {
                  visibleEvents.length
                }
              </strong>
            </div>

            <div
              style={{
                display: 'grid',
                gap: '6px',
                marginTop: '4px',
                fontSize: '13px',
              }}
            >
            <span>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  backgroundColor:
                    '#0066ff',
                  color: '#ffffff',
                  fontWeight: 'bold',
                  marginRight: '7px',
                }}
              >
                ↓
              </span>

              Load
            </span>

            <span>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  backgroundColor:
                    '#00a651',
                  color: '#ffffff',
                  fontWeight: 'bold',
                  marginRight: '7px',
                }}
              >
                ↑
              </span>

              Unload
            </span>

              <span>
                <span
                  style={{
                    display:
                      'inline-block',
                    width: '12px',
                    height: '12px',
                    borderRadius:
                      '50%',
                    backgroundColor:
                      '#777777',
                    marginRight:
                      '6px',
                  }}
                />

                Movement before load
              </span>
            </div>
          </div>
        </aside>

        <section
          className={
            styles.mapSection
          }
        >
          <MapContainer
            center={[
              44.95,
              9.05,
            ]}
            zoom={8}
            className={
              styles.mapContainer
            }
            zoomControl={false}
            maxZoom={18}
            minZoom={5}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution={
                '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              }
            />

            {showRoute &&
              routeSegments.map(
                (
                  segment,
                  index
                ) => (
                  <Polyline
                    key={
                      `route-${selectedDeviceId}-${index}`
                    }
                    positions={
                      segment.positions
                    }
                    pathOptions={{
                      color:
                        segment.color,

                      weight: 6,

                      opacity: 0.9,

                      lineCap:
                        'round',

                      lineJoin:
                        'round',
                    }}
                  >
                    <Popup>
                      <strong>
                        Plate{' '}
                        {
                          selectedDeviceId
                        }
                      </strong>

                      <br />

                      Route status:{' '}

                      <strong
                        style={{
                          color:
                            segment.color,
                        }}
                      >
                        {getEventDescription(
                          segment.activeEvent
                        )}
                      </strong>

                      <br />

                      Segment points:{' '}

                      {
                        segment.positions
                          .length
                      }
                    </Popup>
                  </Polyline>
                )
              )}

            {showEvents &&
            visibleEvents.map(
              (
                position,
                index
              ) => {
                const eventColor =
                  getEventColor(
                    position.event
                  );

                return (
                  <Marker
                    key={
                      `event-${selectedDeviceId}-` +
                      `${position.created_at}-` +
                      `${position.event}-${index}`
                    }
                    position={[
                      position.latitude,
                      position.longitude,
                    ]}
                    icon={createEventIcon(
                      position.event
                    )}
                    zIndexOffset={1000}
                  >
                    <Popup>
                      <strong>
                        Plate{' '}
                        {selectedDeviceId}
                      </strong>

                      <br />

                      Event:{' '}

                      <strong
                        style={{
                          color: eventColor,
                        }}
                      >
                        {getEventDescription(
                          position.event
                        )}
                      </strong>

                      <br />

                      <strong>
                        Probes
                      </strong>

                      <br />

                      Probe 1:{' '}
                      <strong>{Number.isFinite(position.level) ? position.level : 'Not available'}</strong>

                      <br />

                      Probe 2:{' '}
                      <strong>{Number.isFinite(position.level_2) ? position.level_2 : 'Not available'}</strong>

                      <br />

                      Probe 3:{' '}
                      <strong>{Number.isFinite(position.level_3) ? position.level_3 : 'Not available'}</strong>

                      <br />

                      Probe 4:{' '}
                      <strong>{Number.isFinite(position.level_4) ? position.level_4 : 'Not available'}</strong>

                      <br />

                      Probe 5:{' '}
                      <strong>{Number.isFinite(position.level_5) ? position.level_5 : 'Not available'}</strong>

                      <br />

                      Date:{' '}

                      {formatDateTime(
                        position.created_at
                      )}

                      <br />

                      Latitude:{' '}

                      {position.latitude.toFixed(
                        6
                      )}

                      <br />

                      Longitude:{' '}

                      {position.longitude.toFixed(
                        6
                      )}
                    </Popup>
                  </Marker>
                );
              }
            )}

            {selectedDevice &&
              Number.isFinite(
                selectedDevice.latitude
              ) &&
              Number.isFinite(
                selectedDevice.longitude
              ) &&
              selectedDevice.latitude >=
                -90 &&
              selectedDevice.latitude <=
                90 &&
              selectedDevice.longitude >=
                -180 &&
              selectedDevice.longitude <=
                180 && (
                <Marker
                  position={[
                    selectedDevice.latitude,
                    selectedDevice.longitude,
                  ]}
                  icon={
                    isWarningDevice(
                      selectedDevice
                    )
                      ? redIcon
                      : blueIcon
                  }
                >
                  <Popup>
                    <strong>
                      Plate{' '}
                      {
                        selectedDevice.id_device
                      }
                    </strong>

                    <br />

                    Current position

                    <br />

                    Last update:{' '}

                    {formatDateTime(
                      selectedDevice.time_last_update
                    )}

                    <br />

                    Battery:{' '}

                    {getBatteryStatusText(
                      selectedDevice.battery_status,
                      selectedDevice.battery_limit
                    )}

                    <br />

                    Level:{' '}

                    {getLevelStatusText(
                      selectedDevice.level,
                      selectedDevice.level_limit
                    )}

                    <br />

                    Latitude:{' '}

                    {selectedDevice.latitude.toFixed(
                      6
                    )}

                    <br />

                    Longitude:{' '}

                    {selectedDevice.longitude.toFixed(
                      6
                    )}
                  </Popup>
                </Marker>
              )}

            <ZoomControl
              position="topright"
            />
          </MapContainer>
        </section>
      </main>

      {showHistory && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Event history"
          onMouseDown={(
            event
          ) => {
            if (
              event.target ===
              event.currentTarget
            ) {
              setShowHistory(
                false
              );
            }
          }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 5000,
            display: 'flex',
            alignItems: 'center',
            justifyContent:
              'center',
            padding: '24px',
            backgroundColor:
              'rgba(0, 0, 0, 0.55)',
          }}
        >
          <div
            style={{
              width: 'min(1050px, 96vw)',
              maxHeight: '88vh',
              display: 'flex',
              flexDirection:
                'column',
              overflow: 'hidden',
              backgroundColor:
                '#ffffff',
              borderRadius:
                '12px',
              boxShadow:
                '0 20px 60px rgba(0, 0, 0, 0.35)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems:
                  'center',
                justifyContent:
                  'space-between',
                gap: '16px',
                padding:
                  '16px 20px',
                borderBottom:
                  '1px solid #dddddd',
              }}
            >
              <div>
                <h2
                  style={{
                    margin: 0,
                  }}
                >
                  Event history
                </h2>

                <div
                  style={{
                    marginTop:
                      '4px',
                    color: '#666666',
                    fontSize:
                      '14px',
                  }}
                >
                  Plate{' '}
                  {selectedDeviceId ||
                    'not selected'}
                </div>
              </div>

              <button
                type="button"
                onClick={() =>
                  setShowHistory(
                    false
                  )
                }
                aria-label="Close event history"
                style={{
                  minWidth: '42px',
                  minHeight: '42px',
                  border:
                    '1px solid #cccccc',
                  borderRadius:
                    '8px',
                  backgroundColor:
                    '#ffffff',
                  cursor:
                    'pointer',
                  fontSize:
                    '22px',
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>

            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '12px',
                alignItems:
                  'end',
                padding:
                  '14px 20px',
                backgroundColor:
                  '#f6f6f6',
                borderBottom:
                  '1px solid #dddddd',
              }}
            >
              <label>
                From date

                <input
                  type="date"
                  value={dateFrom}
                  max={
                    dateTo ||
                    undefined
                  }
                  onChange={(
                    event
                  ) =>
                    setDateFrom(
                      event.target
                        .value
                    )
                  }
                  style={{
                    display:
                      'block',
                    marginTop:
                      '4px',
                    padding:
                      '7px',
                  }}
                />
              </label>

              <label>
                To date

                <input
                  type="date"
                  value={dateTo}
                  min={
                    dateFrom ||
                    undefined
                  }
                  onChange={(
                    event
                  ) =>
                    setDateTo(
                      event.target
                        .value
                    )
                  }
                  style={{
                    display:
                      'block',
                    marginTop:
                      '4px',
                    padding:
                      '7px',
                  }}
                />
              </label>

              <button
                type="button"
                className={
                  styles.filterButton
                }
                onClick={
                  resetHistoryFilters
                }
              >
                Reset filters
              </button>

              <div
                style={{
                  marginLeft:
                    'auto',
                  fontWeight:
                    'bold',
                }}
              >
                Events:{' '}
                {
                  visibleEvents.length
                }
              </div>
            </div>

            <div
              style={{
                overflow: 'auto',
                padding: '20px',
              }}
            >
              {visibleEvents.length >
              0 ? (
                <table
                  className={
                    styles.warningTable
                  }
                  style={{
                    width: '100%',
                  }}
                >
                  <thead>
                    <tr>
                      <th>#</th>

                      <th>
                        Event
                      </th>

                      <th>Probe 1</th>
                      <th>Probe 2</th>
                      <th>Probe 3</th>
                      <th>Probe 4</th>
                      <th>Probe 5</th>

                      <th>
                        Date and time
                      </th>

                      <th>
                        Latitude
                      </th>

                      <th>
                        Longitude
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {visibleEvents.map(
                      (
                        position,
                        index
                      ) => {
                        const eventColor =
                          getEventColor(
                            position.event
                          );

                        return (
                          <tr
                            key={
                              `${position.created_at}-` +
                              `${position.event}-` +
                              `${index}`
                            }
                          >
                            <td>
                              {
                                index +
                                1
                              }
                            </td>

                            <td>
                              <span
                                style={{
                                  display:
                                    'inline-flex',
                                  alignItems:
                                    'center',
                                  justifyContent:
                                    'center',
                                  width:
                                    '28px',
                                  height:
                                    '28px',
                                  borderRadius:
                                    '50%',
                                  backgroundColor:
                                    eventColor,
                                  color:
                                    '#ffffff',
                                  fontSize:
                                    '19px',
                                  fontWeight:
                                    'bold',
                                  lineHeight:
                                    1,
                                  marginRight:
                                    '8px',
                                  verticalAlign:
                                    'middle',
                                }}
                              >
                                {position.event ===
                                'LOAD'
                                  ? '↓'
                                  : '↑'}
                              </span>

                              <strong
                                style={{
                                  color:
                                    eventColor,
                                  verticalAlign:
                                    'middle',
                                }}
                              >
                                {getEventDescription(
                                  position.event
                                )}
                              </strong>
                            </td>

                            <td>{Number.isFinite(position.level) ? position.level : 'Not available'}</td>
                            <td>{Number.isFinite(position.level_2) ? position.level_2 : 'Not available'}</td>
                            <td>{Number.isFinite(position.level_3) ? position.level_3 : 'Not available'}</td>
                            <td>{Number.isFinite(position.level_4) ? position.level_4 : 'Not available'}</td>
                            <td>{Number.isFinite(position.level_5) ? position.level_5 : 'Not available'}</td>

                            <td>
                              {formatDateTime(
                                position.created_at
                              )}
                            </td>

                            <td>
                              {position.latitude.toFixed(
                                6
                              )}
                            </td>

                            <td>
                              {position.longitude.toFixed(
                                6
                              )}
                            </td>
                          </tr>
                        );
                      }
                    )}
                  </tbody>
                </table>
              ) : (
                <div
                  style={{
                    padding:
                      '40px 20px',
                    textAlign:
                      'center',
                    color:
                      '#666666',
                  }}
                >
                  No events were found
                  for the selected
                  device and date range.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;